import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { TOUR_STEPS, TOUR_STORAGE_KEY, PAGE_TOUR_STORAGE_KEY, tourStepsForMode } from "@/lib/product-tour";
import { useProfile } from "@/lib/profile-cache";
import { modeOf } from "@/lib/doors";

/**
 * Tour state, kept out of the overlay so the overlay only has to draw.
 *
 * Two decisions worth knowing before changing this:
 *
 * 1. Completion lives in localStorage, not on the user record. The tour is a
 *    UI preference, and putting it on the server buys cross-device memory at
 *    the cost of a model field, a migration and an endpoint — for a thing whose
 *    worst failure is being offered a tour twice on a second laptop. The key is
 *    versioned, so materially changing the tour re-offers it.
 *
 * 2. It never auto-starts. An overlay that seizes the screen before a user has
 *    looked at it is the reason people reflexively dismiss tours. The first-run
 *    entry point is an invitation in the sidebar; `hasSeen` is what lets that
 *    invitation quieten down afterwards.
 *
 * 3. One provider runs two kinds of tour: the product tour (the spine, from
 *    the sidebar) and a page tour (that page's components, from the page
 *    header). Whichever is running is `active`; each is remembered on its own,
 *    so replaying one page never resets another, and a page tour does not
 *    count as the product tour.
 */

export const PRODUCT_TOUR_ID = "product";

const TourContext = createContext(null);

/** Reading localStorage can throw — Safari private mode, embedded webviews. A
 *  tour is not worth taking the app down for, so every access is guarded. */
function readSeen() {
  try {
    return window.localStorage.getItem(TOUR_STORAGE_KEY) === "done";
  } catch {
    return false;
  }
}

function readSeenPages() {
  try {
    const raw = window.localStorage.getItem(PAGE_TOUR_STORAGE_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids : []);
  } catch {
    return new Set();
  }
}

function writeSeenPages(set) {
  try {
    window.localStorage.setItem(PAGE_TOUR_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* as above */
  }
}

function writeSeen() {
  try {
    window.localStorage.setItem(TOUR_STORAGE_KEY, "done");
  } catch {
    /* a tour that cannot remember it ran is still a working tour */
  }
}

export function TourProvider({ children, steps = TOUR_STEPS }) {
  // { id, steps, index } while a tour runs; null otherwise.
  const [active, setActive] = useState(null);
  const [seen, setSeen] = useState(readSeen);
  const [seenPages, setSeenPages] = useState(readSeenPages);
  const navigate = useNavigate();
  const location = useLocation();

  const running = active !== null;
  const step = running ? active.steps[active.index] ?? null : null;

  const markSeen = useCallback((id) => {
    if (id === PRODUCT_TOUR_ID) {
      writeSeen();
      setSeen(true);
      return;
    }
    setSeenPages((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      writeSeenPages(next);
      return next;
    });
  }, []);

  const stop = useCallback(() => {
    // Leaving early still quietens the invitation: someone who closed a tour
    // did not want it, and offering it again is how that feeling hardens.
    setActive((cur) => {
      if (cur) markSeen(cur.id);
      return null;
    });
  }, [markSeen]);

  // The opening steps in the words of the user's door; read at start so a
  // profile that loads after mount still gets its own copy.
  const profile = useProfile();
  const mode = modeOf(profile);
  const start = useCallback(
    () => setActive({ id: PRODUCT_TOUR_ID, steps: tourStepsForMode(mode, steps), index: 0 }),
    [steps, mode]);

  // A page tour's steps carry the page's route pattern; pin them to the path
  // being viewed so the navigation effect below treats them as "already here".
  const startPage = useCallback((tour) => {
    const here = location.pathname;
    setActive({
      id: tour.id,
      index: 0,
      steps: tour.steps.map((s) => ({ ...s, route: here, feature: s.feature ?? tour.feature })),
    });
  }, [location.pathname]);

  const next = useCallback(() => {
    setActive((cur) => {
      if (!cur) return cur;
      if (cur.index >= cur.steps.length - 1) {
        // Reaching the end is the one path that counts as completing it.
        markSeen(cur.id);
        return null;
      }
      return { ...cur, index: cur.index + 1 };
    });
  }, [markSeen]);

  const prev = useCallback(() => {
    setActive((cur) => (cur ? { ...cur, index: Math.max(0, cur.index - 1) } : cur));
  }, []);

  // A step can live on a different page than the one being viewed. Navigate
  // toward it rather than pointing at an element that is not mounted.
  useEffect(() => {
    if (!step?.route) return;
    if (location.pathname !== step.route) navigate(step.route);
  }, [step, location.pathname, navigate]);

  // Escape leaves. Arrow keys move. Bound at the document because the target
  // element keeps focus for screen readers, so the card is not always focused.
  useEffect(() => {
    if (!running) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") stop();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [running, stop, next, prev]);

  const total = active ? active.steps.length : steps.length;
  const index = active ? active.index : 0;
  const hasSeenPage = useCallback((id) => seenPages.has(id), [seenPages]);

  const value = useMemo(
    () => ({
      running,
      step,
      index,
      total,
      isFirst: running && index === 0,
      isLast: running && index === total - 1,
      activeTourId: active?.id ?? null,
      hasSeen: seen,
      hasSeenPage,
      start,
      startPage,
      stop,
      next,
      prev,
    }),
    [running, step, index, total, active, seen, hasSeenPage, start, startPage, stop, next, prev]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/**
 * Returns null outside a provider rather than throwing.
 *
 * The launcher renders inside the sidebar, which several tests and the public
 * shell mount without a TourProvider. Throwing there would make an optional
 * feature able to break unrelated pages, so callers check instead.
 */
export function useTour() {
  return useContext(TourContext);
}
